import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AuthAPI } from "../api";
import { useAuth } from "../context/AuthContext";

export default function EditMentorProfile() {
  const { user } = useAuth();
  const [profile, setProfile] = useState({});
  const [preview, setPreview] = useState(null);
  const [emailInfo, setEmailInfo] = useState({ email: "", is_verified: false });
  const [resendStatus, setResendStatus] = useState("");
  const [toast, setToast] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const defaultSvg = `data:image/svg+xml;utf8,${encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="gray">
      <circle cx="12" cy="8" r="4" />
      <path d="M4 20c0-4 3.582-7 8-7s8 3 8 7" />
    </svg>
  `)}`;

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const loadProfile = useCallback(async () => {
    setError(false);
    setLoading(true);
    const [profileResult, authResult] = await Promise.allSettled([
      AuthAPI.get("mentors/"),
      AuthAPI.get("auth/me/"),
    ]);

    if (profileResult.status === "fulfilled") {
      if (profileResult.value.data.length > 0) {
        setProfile(profileResult.value.data[0]);
        setPreview(profileResult.value.data[0].profile_picture || null);
      } else {
        setProfile({});
        setPreview(null);
      }
    } else {
      console.error("Mentor profile load failed", profileResult.reason);
      setError(true);
    }

    if (authResult.status === "fulfilled") {
      setEmailInfo({
        email: authResult.value.data.email,
        is_verified: authResult.value.data.is_verified,
      });
    } else {
      console.error("Error fetching auth info", authResult.reason);
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (files) {
      setProfile({ ...profile, [name]: files[0] });
      setPreview(URL.createObjectURL(files[0]));
    } else {
      setProfile({ ...profile, [name]: value });
    }
  };

  const handleSave = async (e) => {
    if (e) e.preventDefault();

    if (!profile.id) {
      showToast("Profile not loaded yet. Please refresh and try again.", "error");
      return;
    }

    const formData = new FormData();
    for (const key in profile) {
      const value = profile[key];
      if (key === "profile_picture") {
        if (value instanceof File) formData.append(key, value);
        else if (value === null) formData.append(key, "");
      } else if (value !== undefined && value !== null) {
        formData.append(key, value);
      }
    }

    try {
      await AuthAPI.patch(`mentors/${profile.id}/`, formData);
      showToast("Profile updated successfully.");
    } catch (saveError) {
      console.error("Error updating profile:", saveError.response?.data || saveError.message);
      showToast("Failed to save profile. Please try again.", "error");
    }
  };

  const handleResendVerification = async () => {
    try {
      setResendStatus("sending");
      await AuthAPI.post("auth/resend-verification/", { email: emailInfo.email });
      setResendStatus("sent");
    } catch (resendError) {
      console.error(resendError);
      setResendStatus("error");
    }
  };

  const inputClass = "w-full rounded-xl border border-[#d7dced] bg-white px-4 py-3 text-sm text-[#111425] transition placeholder:text-[#9aa1b7] focus:border-[#282E6A] focus:outline-none focus:ring-2 focus:ring-[#282E6A]";
  const readonlyClass = "w-full rounded-xl border border-[#e2e6f2] bg-[#f7f8fc] px-4 py-3 text-sm text-[#68708b]";
  const displayName = [user?.first_name, user?.last_name].filter(Boolean).join(" ");

  const profileStrength = useMemo(() => {
    const fields = [
      profile.college_name,
      profile.major,
      profile.gpa,
      profile.freshman_year,
      profile.graduation_year,
      profile.current_position,
      profile.company,
      profile.expertise,
      profile.linkedin_url,
    ];
    const filled = fields.filter((field) => String(field || "").trim()).length;
    return Math.round((filled / fields.length) * 100);
  }, [profile]);

  if (loading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <svg className="h-8 w-8 animate-spin text-[#282E6A]" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
        </svg>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6">
        <div className="rounded-[28px] border border-red-200 bg-white p-8 text-center shadow-sm">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-red-50 text-red-600">
            <svg className="h-7 w-7" fill="none" viewBox="0 0 24 24" strokeWidth={1.6} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m0 3.75h.008v.008H12v-.008zm8.25-3.75a8.25 8.25 0 11-16.5 0 8.25 8.25 0 0116.5 0z" />
            </svg>
          </div>
          <h1 className="mt-4 text-2xl font-semibold text-[#111425]">Mentor profile did not load</h1>
          <p className="mx-auto mt-3 max-w-xl text-sm leading-7 text-[#5d647f]">
            The mentor profile service is unavailable right now. Retry before making edits.
          </p>
          <button
            onClick={loadProfile}
            className="mt-5 inline-flex items-center justify-center rounded-xl bg-[#282E6A] px-5 py-2.5 text-sm font-medium text-white transition hover:bg-[#1e2458]"
          >
            Retry profile
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl space-y-8 px-4 py-8 sm:px-6">
      {toast && (
        <div className={`flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium ${
          toast.type === "success"
            ? "border border-green-200 bg-green-50 text-green-800"
            : "border border-red-200 bg-red-50 text-red-800"
        }`}>
          {toast.type === "success" ? (
            <svg className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          ) : (
            <svg className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          )}
          {toast.message}
        </div>
      )}

      <section className="overflow-hidden rounded-[30px] border border-[#dfe4f2] bg-white/85 shadow-[0_28px_70px_-48px_rgba(23,30,67,0.45)] backdrop-blur-sm">
        <div className="grid gap-6 px-6 py-7 lg:grid-cols-[1.08fr_0.92fr] lg:px-8">
          <div>
            <span className="inline-flex rounded-full border border-[#cfd5ee] bg-[#eef0fa] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.24em] text-[#282E6A]">
              Mentor profile
            </span>
            <h1 className="mt-4 text-3xl leading-tight text-[#111425] sm:text-4xl">
              Present your experience like someone worth booking, not just someone available.
            </h1>
            <p className="mt-3 max-w-2xl text-sm leading-7 text-[#5d647f] sm:text-base">
              Your profile sets expectations before the first call. Strong academic detail and focused expertise make the right students trust you faster.
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <Link
                to="/dashboard"
                className="inline-flex items-center justify-center rounded-xl bg-[#282E6A] px-5 py-2.5 text-sm font-medium text-white transition hover:bg-[#1e2458]"
              >
                Open dashboard
              </Link>
              <Link
                to="/calendar"
                className="inline-flex items-center justify-center rounded-xl border border-[#cfd5ee] bg-white px-5 py-2.5 text-sm font-medium text-[#282E6A] transition hover:bg-[#eef0fa]"
              >
                Open calendar
              </Link>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-1">
            {[
              { label: "Profile strength", value: `${profileStrength}%`, tone: "bg-[#111425] text-white border-[#202640]" },
              { label: "Total earnings", value: profile.earnings ?? 0, tone: "bg-[#eef0fa] text-[#111425] border-[#d9def3]" },
              { label: "Verification", value: emailInfo.is_verified ? "Verified" : "Pending", tone: "bg-[#f5f1e7] text-[#111425] border-[#ead9b7]" },
            ].map((stat) => (
              <div key={stat.label} className={`rounded-[22px] border p-4 ${stat.tone}`}>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] opacity-70">{stat.label}</p>
                <p className="mt-3 text-3xl font-semibold">{stat.value}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-[0.74fr_1.26fr]">
        <aside className="rounded-[28px] border border-[#dfe4f2] bg-white/92 p-6 shadow-[0_22px_60px_-44px_rgba(23,30,67,0.35)]">
          <div className="flex flex-col items-center text-center">
            <div className="relative">
              <img
                src={preview || defaultSvg}
                alt="Profile"
                className="h-28 w-28 rounded-full border border-[#dfe4f2] object-cover shadow-sm"
              />
              {emailInfo.is_verified && (
                <span className="absolute -bottom-1 -right-1 rounded-full border border-white bg-green-500 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-white">
                  Verified
                </span>
              )}
            </div>
            <h2 className="mt-5 text-2xl font-semibold text-[#111425]">{displayName || "Mentor profile"}</h2>
            <p className="mt-1 text-sm text-[#5d647f]">{emailInfo.email}</p>
          </div>

          <div className="mt-6 space-y-4">
            <div className="rounded-[22px] border border-[#e3e7f3] bg-[#f7f8fc] p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#6d7490]">Account status</p>
              {emailInfo.is_verified ? (
                <p className="mt-2 text-sm font-medium text-green-700">Email verified and ready to appear credibly in search.</p>
              ) : (
                <div className="mt-2 space-y-2">
                  <p className="text-sm font-medium text-[#111425]">Email verification is still pending.</p>
                  <button
                    type="button"
                    onClick={handleResendVerification}
                    className="text-sm font-medium text-[#282E6A] underline underline-offset-4"
                    disabled={resendStatus === "sending"}
                  >
                    {resendStatus === "sending" ? "Sending…" : "Resend verification email"}
                  </button>
                  {resendStatus === "sent" && (
                    <p className="text-sm text-green-600">Verification email sent. Check your inbox.</p>
                  )}
                  {resendStatus === "error" && (
                    <p className="text-sm text-red-600">Could not resend verification email.</p>
                  )}
                </div>
              )}
            </div>

            <div className="rounded-[22px] border border-[#e8dcc0] bg-[#f7f2e6] p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#8d6a2a]">Booking signal</p>
              <p className="mt-2 text-sm leading-6 text-[#111425]">
                Students decide fast. A specific major, university, and expertise line does more work than a generic profile ever will.
              </p>
            </div>

            {preview && (
              <button
                type="button"
                className="w-full rounded-xl border border-red-200 px-4 py-3 text-sm font-medium text-red-600 transition hover:bg-red-50"
                onClick={() => {
                  setPreview(null);
                  setProfile((prev) => ({ ...prev, profile_picture: null }));
                }}
              >
                Remove profile photo
              </button>
            )}
          </div>
        </aside>

        <form onSubmit={handleSave} className="space-y-6 rounded-[28px] border border-[#dfe4f2] bg-white/92 p-6 shadow-[0_22px_60px_-44px_rgba(23,30,67,0.35)]">
          <section className="rounded-[22px] border border-[#e4e8f4] bg-[#f7f8fc] p-5">
            <h3 className="text-lg font-semibold text-[#111425]">Account</h3>
            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-[#4f5670]">Full name</label>
                <input value={displayName} readOnly className={readonlyClass} />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-[#4f5670]">Total earnings</label>
                <input value={profile.earnings ?? 0} readOnly className={readonlyClass} />
              </div>
            </div>
          </section>

          <section className="rounded-[22px] border border-[#eceef6] bg-white p-5">
            <h3 className="text-lg font-semibold text-[#111425]">Profile image</h3>
            <p className="mt-2 text-sm leading-7 text-[#5d647f]">
              A clear profile image makes your page feel trustworthy and ready for booking.
            </p>
            <input
              type="file"
              name="profile_picture"
              accept="image/*"
              onChange={handleChange}
              className="mt-4 block w-full text-sm text-[#6a728d] file:mr-4 file:rounded-xl file:border-0 file:bg-[#eef0fa] file:px-4 file:py-3 file:text-sm file:font-medium file:text-[#282E6A] hover:file:bg-[#dde0f7]"
            />
          </section>

          <section className="rounded-[22px] border border-[#eceef6] bg-white p-5">
            <h3 className="text-lg font-semibold text-[#111425]">Academic background</h3>
            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-[#4f5670]">College Name</label>
                <input
                  name="college_name"
                  placeholder="e.g. Stanford University"
                  value={profile.college_name || ""}
                  onChange={handleChange}
                  className={inputClass}
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-[#4f5670]">Major / Field of Study</label>
                <input
                  name="major"
                  placeholder="e.g. Computer Science"
                  value={profile.major || ""}
                  onChange={handleChange}
                  className={inputClass}
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-[#4f5670]">GPA</label>
                <input
                  name="gpa"
                  placeholder="e.g. 3.7 / 4.0"
                  value={profile.gpa || ""}
                  onChange={handleChange}
                  className={inputClass}
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-[#4f5670]">College Start Year</label>
                <input
                  name="freshman_year"
                  placeholder="e.g. 2021"
                  value={profile.freshman_year || ""}
                  onChange={handleChange}
                  className={inputClass}
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-[#4f5670]">Graduation Year</label>
                <input
                  name="graduation_year"
                  placeholder="e.g. 2025"
                  value={profile.graduation_year || ""}
                  onChange={handleChange}
                  className={inputClass}
                />
              </div>
            </div>
          </section>

          <section className="rounded-[22px] border border-[#eceef6] bg-white p-5">
            <h3 className="text-lg font-semibold text-[#111425]">Professional background</h3>
            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-[#4f5670]">Current Position</label>
                <input
                  name="current_position"
                  placeholder="e.g. Software Engineer"
                  value={profile.current_position || ""}
                  onChange={handleChange}
                  className={inputClass}
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-[#4f5670]">Company</label>
                <input
                  name="company"
                  placeholder="e.g. Google"
                  value={profile.company || ""}
                  onChange={handleChange}
                  className={inputClass}
                />
              </div>
            </div>
          </section>

          <section className="rounded-[22px] border border-[#eceef6] bg-white p-5">
            <h3 className="text-lg font-semibold text-[#111425]">Expertise & online presence</h3>
            <div className="mt-4 space-y-4">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-[#4f5670]">Areas of Expertise</label>
                <textarea
                  name="expertise"
                  placeholder="e.g. College applications, DSA, internship prep"
                  value={profile.expertise || ""}
                  onChange={handleChange}
                  rows={3}
                  className={`${inputClass} resize-none`}
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-[#4f5670]">LinkedIn Profile</label>
                <input
                  name="linkedin_url"
                  placeholder="https://linkedin.com/in/username"
                  value={profile.linkedin_url || ""}
                  onChange={handleChange}
                  className={inputClass}
                />
              </div>
            </div>
          </section>

          <button
            type="submit"
            className="w-full rounded-xl bg-[#282E6A] py-3 text-sm font-medium text-white transition-colors hover:bg-[#1e2458]"
          >
            Save changes
          </button>
        </form>
      </section>
    </div>
  );
}
